import React, { FC, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchOrder, GiftOrder, planName } from './giftApi';
import './Gift.css';

const POLL_MS = 2000;
const MAX_POLLS = 15; // about 30 seconds; the webhook usually finishes in a few

const formatDate = (epochSeconds: number, timeZone?: string | null) =>
  new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: timeZone ?? undefined,
  }).format(new Date(epochSeconds * 1000));

export const GiftThanks: FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') ?? '';
  const [order, setOrder] = useState<GiftOrder | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    try { sessionStorage.removeItem('toastie-gift-draft'); } catch { /* fine */ }

    let polls = 0;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      polls++;
      try {
        const result = await fetchOrder(sessionId);
        if (stopped) return;
        if (result.status !== 'pending') {
          setOrder(result);
          return;
        }
      } catch {
        // Treat a failed lookup like "not ready yet".
      }
      if (polls >= MAX_POLLS) setTimedOut(true);
      else timer = setTimeout(poll, POLL_MS);
    };

    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [sessionId]);

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked: the code is visible anyway */ }
  };

  const ready = order?.status === 'ready' ? order : null;
  const now = Date.now() / 1000;

  return (
    <div className='gift-page'>
      <Helmet>
        <title>Thank you for your gift — Toastie Health</title>
        <meta name='robots' content='noindex' />
      </Helmet>

      <div className='gift-card gift-thanks'>
        <p className='gift-eyebrow'>Gift confirmed</p>

        {!sessionId ? (
          <>
            <h1 className='gift-title-sm'>Looking for your gift?</h1>
            <p className='gift-text'>
              Your gift code is in your confirmation email. Can't find it? Email{' '}
              <a href='mailto:hello@toastie.au'>hello@toastie.au</a> and we'll help.
            </p>
          </>
        ) : order?.status === 'refunded' || order?.status === 'disputed' ? (
          <>
            <h1 className='gift-title-sm'>This gift has been refunded</h1>
            <p className='gift-text'>
              The payment was refunded, so this gift code no longer works. Questions? Email{' '}
              <a href='mailto:hello@toastie.au'>hello@toastie.au</a>.
            </p>
          </>
        ) : ready ? (
          <>
            <h1 className='gift-title-sm'>Thank you for your gift 💛</h1>
            <p className='gift-text'>
              You've gifted <strong>{planName(ready.plan_months)} of Toastie Pro</strong>.{' '}
              {!ready.recipient_email
                ? "We've emailed you the code so you can pass it on."
                : ready.delivered || ready.deliver_at <= now + 60
                  ? <>We've emailed it to <strong>{ready.recipient_name || ready.recipient_email}</strong>.</>
                  : <>We'll email it to <strong>{ready.recipient_name || ready.recipient_email}</strong> on the
                      morning of <strong>{formatDate(ready.deliver_at, ready.delivery_timezone)}</strong>.</>}
            </p>

            <div className='gift-code-box'>
              <span className='gift-code-label'>Gift code</span>
              <span className='gift-code'>{ready.code}</span>
              <button type='button' className='gift-copy' onClick={() => copyCode(ready.code)}>
                {copied ? 'Copied' : 'Copy code'}
              </button>
              <span className='gift-code-expiry'>Valid until {formatDate(ready.expires_at)}</span>
            </div>

            <p className='gift-text gift-muted'>
              Keep this page or your confirmation email. They redeem at{' '}
              <a href={`/redeem?code=${encodeURIComponent(ready.code)}`}>toastie.au/redeem</a>.
            </p>
            <Link className='button gift-submit gift-again' to='/gift'>Give another gift</Link>
          </>
        ) : timedOut ? (
          <>
            <h1 className='gift-title-sm'>Payment received, thank you 💛</h1>
            <p className='gift-text'>
              We're just finishing your gift. Your confirmation email, with the gift code, will arrive within a
              few minutes. If it hasn't come in 15 minutes, check your spam folder or email{' '}
              <a href='mailto:hello@toastie.au'>hello@toastie.au</a>.
            </p>
          </>
        ) : (
          <>
            <h1 className='gift-title-sm'>Preparing your gift…</h1>
            <p className='gift-text gift-muted'>This only takes a few seconds.</p>
            <div className='gift-spinner' aria-hidden />
          </>
        )}
      </div>
    </div>
  );
};
